import { HttpClientXsrfModule } from '@angular/common/http';
import {
  ErrorHandler,
  NgModule,
  PLATFORM_ID,
  Testability,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { GoogleMapsModule } from '@angular/google-maps';
import { BrowserModule } from '@angular/platform-browser';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { RouteReuseStrategy } from '@angular/router';
import {
  ABTEST_WHITELIST_COOKIE,
  ABTest,
  ABTestOverrides,
} from '@freelancer/abtest';
import { ABTestTesting } from '@freelancer/abtest/testing';
import { Accessibility } from '@freelancer/accessibility';
import { AccessibilityTesting } from '@freelancer/accessibility/testing';
import { AccountLinkingService } from '@freelancer/account-linking';
import { AccountLinkingTestingService } from '@freelancer/account-linking/testing';
import { ApplePay } from '@freelancer/apple-pay';
import { ApplePayTesting } from '@freelancer/apple-pay/testing';
import { AudioPlayer } from '@freelancer/audio-player';
import { AudioPlayerTesting } from '@freelancer/audio-player/testing';
import { AUTH_CONFIG, AppleSSO, Auth, AuthModule } from '@freelancer/auth';
import { AppleSSOTesting, AuthTesting } from '@freelancer/auth/testing';
import { BILLING_CONFIG } from '@freelancer/billing';
import { BrowserPermissions } from '@freelancer/browser-permissions';
import { BrowserPermissionsTesting } from '@freelancer/browser-permissions/testing';
import { CAPTCHA_CONFIG } from '@freelancer/captcha';
import { ComponentsModalsModule } from '@freelancer/components/components-modals.module';
import { FileViewerModule } from '@freelancer/components/file-viewer';
import {
  APPS_DOMAINS_MAP,
  APP_NAME,
  BLANK_STATE,
  ENVIRONMENT_NAME,
  SITE_NAME,
  USE_FAKES,
} from '@freelancer/config';
import { CookieTesting } from '@freelancer/cookie/testing';
import {
  CustomFieldsValidator,
  CustomFieldsValidatorTesting,
} from '@freelancer/custom-fields';
import { datastoreActionSanitizer } from '@freelancer/datastore';
import { datastoreInitializer } from '@freelancer/datastore/collections-testing';
import { DatastoreModule } from '@freelancer/datastore/core/datastore.module';
import { DatastoreFakeModule } from '@freelancer/datastore/testing';
import { Deposits } from '@freelancer/deposits';
import { DepositsTesting } from '@freelancer/deposits/testing';
import { EmailVerification } from '@freelancer/email-verification';
import { EmailVerificationTesting } from '@freelancer/email-verification/testing';
import {
  FaceFilterService,
  FaceFilterTestingService,
} from '@freelancer/face-filter';
import { FACEBOOK_CONFIG, Facebook } from '@freelancer/facebook';
import { FacebookTesting } from '@freelancer/facebook/testing';
import { FileDownload, FileDownloadModule } from '@freelancer/file-download';
import { FileDownloadTesting } from '@freelancer/file-download/testing';
import { FileRead } from '@freelancer/file-read';
import { FileReadTesting } from '@freelancer/file-read/testing';
import { FileStorage } from '@freelancer/file-storage';
import { FileStorageTesting } from '@freelancer/file-storage/testing';
import {
  FileUploadLegacy,
  FileUploadLegacyModule,
} from '@freelancer/file-upload-legacy';
import { FileUploadLegacyTesting } from '@freelancer/file-upload-legacy/testing';
import {
  FREELANCER_HTTP_ABTEST_OVERRIDES_PROVIDER,
  FREELANCER_HTTP_AUTH_PROVIDERS,
  FREELANCER_HTTP_CONFIG,
  FREELANCER_HTTP_TRACKING_PROVIDER,
  FreelancerHttp,
  SENTRY_TRACING_PROVIDER,
} from '@freelancer/freelancer-http';
import { BackgroundGeolocation } from '@freelancer/geolocation';
import { GeolocationTesting } from '@freelancer/geolocation/testing';
import { GOOGLE_MAPS_CONFIG, GoogleMaps } from '@freelancer/google-maps';
import { GoogleMapsTesting } from '@freelancer/google-maps/testing';
import {
  GOOGLE_SIGN_IN_CONFIG,
  GoogleSignInService,
} from '@freelancer/google-sign-in';
import { GoogleSignInServiceTesting } from '@freelancer/google-sign-in/testing';
import { Haptics, HapticsTestingService } from '@freelancer/haptics';
import { InAppBrowser, InAppBrowserModule } from '@freelancer/in-app-browser';
import { InAppBrowserTesting } from '@freelancer/in-app-browser/testing';
import { LinkshimService, LinkshimServiceTesting } from '@freelancer/linkshim';
import { LocalStorage } from '@freelancer/local-storage';
import { LocalStorageTesting } from '@freelancer/local-storage/testing';
import {
  NATIVE_LANGUAGE_COOKIE,
  WEBAPP_LANGUAGE_COOKIE,
} from '@freelancer/localization';
import {
  FREELANCER_LOCATION_AUTH_PROVIDER,
  FREELANCER_LOCATION_HTTP_BASE_URL_PROVIDER,
  FREELANCER_LOCATION_IN_APP_BROWSER_PROVIDER,
  FREELANCER_LOCATION_PWA_PROVIDER,
  FREELANCER_LOCATION_TRACKING_CONSENT_PROVIDER,
  Location,
} from '@freelancer/location';
import { LocationTesting } from '@freelancer/location/testing';
import { LOGIN_CONFIG } from '@freelancer/login';
import {
  LoginSignupModule,
  LoginSignupService,
} from '@freelancer/login-signup';
import { LoginSignupTesting } from '@freelancer/login-signup/testing';
import { MediaDevices } from '@freelancer/media-devices';
import { MediaDevicesTesting } from '@freelancer/media-devices/testing';
import { MESSAGING_CONFIG } from '@freelancer/messaging-chat';
import { PendingTasks, PendingTasksTesting } from '@freelancer/pending-tasks';
import { PhoneVerification } from '@freelancer/phone-verification';
import { PhoneVerificationTesting } from '@freelancer/phone-verification/testing';
import { PreloadRoute } from '@freelancer/preload-route';
import { PreloadRouteTesting } from '@freelancer/preload-route/testing';
import { FREELANCER_PWA_TRACKING_PROVIDER, Pwa } from '@freelancer/pwa';
import { RandomWordsService } from '@freelancer/random-words';
import { RandomWordsTestingService } from '@freelancer/random-words/testing';
import { RecentlyViewedService } from '@freelancer/recently-viewed';
import { RecentlyViewedServiceTesting } from '@freelancer/recently-viewed/testing';
import { RedirectToPhp } from '@freelancer/redirect-to-php';
import { RedirectToPhpTesting } from '@freelancer/redirect-to-php/testing';
import { ReviseTextService } from '@freelancer/revise-text';
import { ReviseTextServiceTesting } from '@freelancer/revise-text/testing';
import { RichMessagesRequests } from '@freelancer/rich-messages-requests';
import { RichMessagesRequestsTesting } from '@freelancer/rich-messages-requests/testing';
import { FlRouteReuseStrategy } from '@freelancer/route-reuse-strategy';
import { SEO_CONFIG } from '@freelancer/seo';
import { SpeechToText } from '@freelancer/speech-to-text';
import { SpeechToTextTesting } from '@freelancer/speech-to-text/testing';
import { ThreatmetrixModule } from '@freelancer/threatmetrix';
import {
  CoreWebVitalsTracking,
  CustomErrorHandler,
  ErrorTracking,
  FacebookPixelTracking,
  GoogleTracking,
  Hotjar,
  MemoryTracking,
  PerformanceTracking,
  TRACKING_CONFIG,
  Tracking,
  TrackingConsent,
  TrackingModule,
} from '@freelancer/tracking';
import {
  CoreWebVitalsTrackingTesting,
  ErrorHandlerTesting,
  ErrorTrackingTesting,
  FacebookPixelTrackingTesting,
  GoogleTrackingTesting,
  HotjarTesting,
  MemoryTrackingTesting,
  PerformanceTrackingTesting,
  TestabilityTesting,
  TrackingTesting,
} from '@freelancer/tracking/testing';
import { UiModule } from '@freelancer/ui';
import { ModalModule } from '@freelancer/ui/modal';
import { Permissions } from '@freelancer/ui/permissions';
import { PermissionsTestingService } from '@freelancer/ui/permissions/testing';
import { UiModalsModule } from '@freelancer/ui/ui-modals.module';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import type { ActionSanitizer } from '@ngrx/store-devtools/src/config';
import { getDomainsMap } from 'applications';
import { COOKIE_OPTIONS, CookieModule, CookieService } from 'ngx-cookie';
import { environment } from '../environments/environment';
import { AppModalsModule } from './app-modals.module';
import { ShellConfig, ShellConfigTesting } from './app-shell';
import { cookieOptionsFactory } from './cookie-options.factory';
/* The type for ActionSanitizer doesn't allow a custom action type.
 * ActionSanitizer probably should be
 *  export declare type ActionSanitizer<A extends Action = Action> = (
 *    action: A,
 *    id: number,
 *  ) => A;
 * but it isn't so let's cast through `any` for now.
 */

/* The type for ActionSanitizer doesn't allow a custom action type.
 * ActionSanitizer probably should be
 *  export declare type ActionSanitizer<A extends Action = Action> = (
 *    action: A,
 *    id: number,
 *  ) => A;
 * but it isn't so let's cast through `any` for now.
 */
const actionSanitizer: ActionSanitizer = datastoreActionSanitizer as any;

const disableAnimations =
  (globalThis.document &&
    !('animate' in (globalThis.document as any).documentElement)) ||
  (globalThis.navigator &&
    /iPhone OS (8|9|10|11|12|13)_/.test(
      (globalThis.navigator as any).userAgent,
    ));

// /!\ DO NOT ADD ANYTHING IN THERE WITHOUT TALKING TO FRONTEND INFRA FIRST /!\
// Application-wide code should be avoided as much as possible; does your code
// needs:
// - to be loaded on ALL logged-in pages?
// - AND to be loaded on ALL logged-out pages?
// - AND to be loaded on the Admin?
// - ...
// Probably not.
@NgModule({
  imports: [
    AuthModule,
    BrowserModule.withServerTransition({ appId: 'webapp' }),
    BrowserAnimationsModule.withConfig({ disableAnimations }),
    FormsModule,
    HttpClientXsrfModule.disable(), // no part of the app should rely on that
    AppModalsModule,
    ComponentsModalsModule,
    InAppBrowserModule,
    UiModalsModule,
    GoogleMapsModule,
    UiModule.initialize({
      assetsBaseUrl: environment.uiConfig.assetsBaseUrl,
      theme: environment.uiConfig.theme,
      lightGalleryLicenseKey: environment.lightGallery.licenseKey,
    }),
    ModalModule,
    CookieModule.withOptions(),
    ThreatmetrixModule.initialize({
      profilingDomain: environment.threatmetrixConfig.profilingDomain,
      orgId: environment.threatmetrixConfig.orgId,
    }),
    environment.useFakes
      ? environment.blankState
        ? DatastoreFakeModule.initialize({
            debug: true,
            initializer: datastoreInitializer({ blankState: true }),
          })
        : DatastoreFakeModule.initialize({
            debug: true,
            initializer: datastoreInitializer(),
          })
      : DatastoreModule.initialize({
          webSocketUrl: environment.datastoreConfig.webSocketUrl,
          enableStoreFreeze: environment.datastoreConfig.enableStoreFreeze,
          httpAdapter: FreelancerHttp,
          requestData: {},
        }),
    !environment.production
      ? StoreDevtoolsModule.instrument({
          maxAge: 150,
          actionSanitizer,
          actionsBlocklist: ['LOCAL_CACHE_FETCH_SUCCESS'],
        })
      : [],
    TrackingModule,
    FileUploadLegacyModule,
    LoginSignupModule,
    FileDownloadModule,
    FileViewerModule,
  ],
  providers: [
    {
      provide: RouteReuseStrategy,
      useClass: FlRouteReuseStrategy,
    },
    {
      provide: AUTH_CONFIG,
      useValue: {
        baseUrl: environment.authConfig.baseUrl,
        switchAccountHackyEndpoint:
          environment.authConfig.switchAccountHackyEndpoint,
        authHashCookie: environment.authConfig.authHashCookie,
        userIdCookie: environment.authConfig.userIdCookie,
        csrfCookie: environment.authConfig.csrfCookie,
        gdprCookie: environment.authConfig.gdprCookie,
        authHeaderName: environment.authConfig.authHeaderName,
        referralCookie: environment.authConfig.referralCookie,
        authIosKeychainToken: environment.authConfig.authIosKeychainToken,
      },
    },
    /**
     * This should be kept mostly consistent with the service overrides in
     * `ui-tests-common/src/setup.ts`. Disable individual service overrides if you
     * need to access the real service in fake environment.
     * e.g. if you want to access real browser permissions.
     */
    environment.useFakes
      ? [
          { provide: ABTest, useClass: ABTestTesting },
          {
            provide: AccountLinkingService,
            useClass: AccountLinkingTestingService,
          },
          { provide: AppleSSO, useClass: AppleSSOTesting },
          { provide: AudioPlayer, useClass: AudioPlayerTesting },
          { provide: Auth, useClass: AuthTesting },
          { provide: BrowserPermissions, useClass: BrowserPermissionsTesting },
          { provide: CookieService, useClass: CookieTesting },
          {
            provide: CustomFieldsValidator,
            useClass: CustomFieldsValidatorTesting,
          },
          { provide: Deposits, useClass: DepositsTesting },
          { provide: EmailVerification, useClass: EmailVerificationTesting },
          { provide: ErrorHandler, useClass: ErrorHandlerTesting },
          { provide: Facebook, useClass: FacebookTesting },
          { provide: FaceFilterService, useClass: FaceFilterTestingService },
          { provide: FileDownload, useClass: FileDownloadTesting },
          { provide: FileUploadLegacy, useClass: FileUploadLegacyTesting },
          { provide: FileRead, useClass: FileReadTesting },
          { provide: FileStorage, useClass: FileStorageTesting },
          { provide: Geolocation, useClass: GeolocationTesting },
          { provide: GoogleMaps, useClass: GoogleMapsTesting },
          {
            provide: GoogleSignInService,
            useClass: GoogleSignInServiceTesting,
          },
          { provide: Haptics, useClass: HapticsTestingService },
          { provide: InAppBrowser, useClass: InAppBrowserTesting },
          { provide: LinkshimService, useClass: LinkshimServiceTesting },
          { provide: LocalStorage, useClass: LocalStorageTesting },
          { provide: Location, useClass: LocationTesting },
          { provide: LoginSignupService, useClass: LoginSignupTesting },
          { provide: MediaDevices, useClass: MediaDevicesTesting },
          { provide: SpeechToText, useClass: SpeechToTextTesting },
          { provide: PendingTasks, useClass: PendingTasksTesting },
          { provide: PhoneVerification, useClass: PhoneVerificationTesting },
          { provide: PreloadRoute, useClass: PreloadRouteTesting },
          { provide: Accessibility, useClass: AccessibilityTesting },
          {
            provide: Permissions,
            useClass: PermissionsTestingService,
          },
          {
            provide: RandomWordsService,
            useClass: RandomWordsTestingService,
          },
          {
            provide: RecentlyViewedService,
            useClass: RecentlyViewedServiceTesting,
          },
          { provide: RedirectToPhp, useClass: RedirectToPhpTesting },
          { provide: ReviseTextService, useClass: ReviseTextServiceTesting },
          {
            provide: RichMessagesRequests,
            useClass: RichMessagesRequestsTesting,
          },
          { provide: ShellConfig, useClass: ShellConfigTesting },
          // Tracking Services
          {
            provide: CoreWebVitalsTracking,
            useClass: CoreWebVitalsTrackingTesting,
          },
          { provide: ErrorTracking, useClass: ErrorTrackingTesting },
          { provide: GoogleTracking, useClass: GoogleTrackingTesting },
          { provide: MemoryTracking, useClass: MemoryTrackingTesting },
          {
            provide: FacebookPixelTracking,
            useClass: FacebookPixelTrackingTesting,
          },
          { provide: Hotjar, useClass: HotjarTesting },
          {
            provide: PerformanceTracking,
            useClass: PerformanceTrackingTesting,
          },
          { provide: Testability, useExisting: TestabilityTesting },
          { provide: Tracking, useClass: TrackingTesting },
          { provide: SENTRY_TRACING_PROVIDER, useClass: ErrorTrackingTesting },
          { provide: ApplePay, useClass: ApplePayTesting },
        ]
      : [],
    { provide: ErrorHandler, useClass: CustomErrorHandler },
    {
      provide: CAPTCHA_CONFIG,
      useValue: {
        recaptchaPublicKey: environment.captchaConfig.recaptchaPublicKey,
      },
    },
    {
      provide: COOKIE_OPTIONS,
      deps: [PLATFORM_ID, Location],
      useFactory: cookieOptionsFactory,
    },
    { provide: USE_FAKES, useValue: environment.useFakes },
    { provide: BLANK_STATE, useValue: environment.blankState },
    { provide: SITE_NAME, useValue: environment.siteName },
    {
      provide: SEO_CONFIG,
      useValue: {
        linkTags: {
          rss: {
            rel: environment.seoConfig.linkTags.rss.rel,
            type: environment.seoConfig.linkTags.rss.type,
            title: environment.seoConfig.linkTags.rss.title,
            href: environment.seoConfig.linkTags.rss.href,
          },
        },
        metaTags: {
          robots: {
            name: environment.seoConfig.metaTags.robots.name,
            content: environment.seoConfig.metaTags.robots.content,
          },
          alexa: {
            name: environment.seoConfig.metaTags.alexa.name,
            content: environment.seoConfig.metaTags.alexa.content,
          },
          bing: {
            name: environment.seoConfig.metaTags.bing.name,
            content: environment.seoConfig.metaTags.bing.content,
          },
        },
        defaultMetaImage: environment.seoConfig.defaultMetaImage,
      },
    },
    {
      provide: GOOGLE_MAPS_CONFIG,
      useValue: {
        apiKey: environment.mapsConfig.apiKey,
      },
    },
    {
      provide: APPS_DOMAINS_MAP,
      useValue: getDomainsMap(environment.environmentName),
    },
    { provide: ENVIRONMENT_NAME, useValue: environment.environmentName },
    { provide: APP_NAME, useValue: environment.appName },
    {
      provide: FREELANCER_HTTP_CONFIG,
      useValue: {
        baseUrl: environment.freelancerHttpConfig.baseUrl,
        apiBaseUrl: environment.freelancerHttpConfig.apiBaseUrl,
        ajaxBaseUrl: environment.freelancerHttpConfig.ajaxBaseUrl,
        authBaseUrl: environment.freelancerHttpConfig.authBaseUrl,
        fileServiceBaseUrl: environment.freelancerHttpConfig.fileServiceBaseUrl,
      },
    },
    { provide: FREELANCER_HTTP_AUTH_PROVIDERS, useExisting: Auth, multi: true },
    {
      provide: FREELANCER_HTTP_ABTEST_OVERRIDES_PROVIDER,
      useExisting: ABTestOverrides,
    },
    {
      provide: FREELANCER_HTTP_TRACKING_PROVIDER,
      useExisting: Tracking,
    },
    {
      provide: FREELANCER_LOCATION_AUTH_PROVIDER,
      useExisting: Auth,
    },
    {
      provide: FREELANCER_LOCATION_HTTP_BASE_URL_PROVIDER,
      useValue: environment.freelancerHttpConfig.baseUrl,
    },
    {
      provide: FREELANCER_LOCATION_PWA_PROVIDER,
      useExisting: Pwa,
    },
    {
      provide: FREELANCER_LOCATION_TRACKING_CONSENT_PROVIDER,
      useExisting: TrackingConsent,
    },
    {
      provide: FREELANCER_PWA_TRACKING_PROVIDER,
      useExisting: Tracking,
    },
    {
      provide: FREELANCER_LOCATION_IN_APP_BROWSER_PROVIDER,
      useExisting: InAppBrowser,
    },
    {
      provide: SENTRY_TRACING_PROVIDER,
      useExisting: ErrorTracking,
    },
    { provide: ABTEST_WHITELIST_COOKIE, useValue: environment.whitelistCookie },
    {
      provide: FACEBOOK_CONFIG,
      useValue: {
        appId: environment.facebookConfig.appId,
      },
    },
    {
      provide: GOOGLE_SIGN_IN_CONFIG,
      useValue: {
        clientId: environment.googleSignInConfig.clientId,
      },
    },
    {
      provide: BILLING_CONFIG,
      useValue: {
        baseUrl: environment.billingConfig.baseUrl,
      },
    },
    {
      provide: WEBAPP_LANGUAGE_COOKIE,
      useValue: environment.languageCookies.webappCookie,
    },
    {
      provide: NATIVE_LANGUAGE_COOKIE,
      useValue: environment.languageCookies.nativeCookie,
    },
    {
      provide: LOGIN_CONFIG,
      useValue: {
        loggedOutRestricted: environment.loginConfig.loggedOutRestricted,
        defaultLogoutRedirectUrl:
          environment.loginConfig.defaultLogoutRedirectUrl,
        ssoIntegration: environment.loginConfig.ssoIntegration,
      },
    },
    {
      provide: MESSAGING_CONFIG,
      useValue: {
        aiChatUserId: environment.messaging.aiChatUserId,
      },
    },
    {
      provide: TRACKING_CONFIG,
      useValue: {
        trackingEndpoint: environment.trackingConfig.trackingEndpoint,
        linkSessionsEndpoint: environment.trackingConfig.linkSessionsEndpoint,
        sessionCookie: environment.trackingConfig.sessionCookie,
        linkedSessionCookie: environment.trackingConfig.sessionCookie,
        clientKey: environment.trackingConfig.clientKey,
        navigationPerformanceSamplingRate:
          environment.trackingConfig.navigationPerformanceSamplingRate,
        enableErrorTracking: environment.trackingConfig.enableErrorTracking,
        sentryDsn: environment.trackingConfig.sentryDsn,
        kibanaBaseUrl: environment.trackingConfig.kibanaBaseUrl,
        gaTrackingId: environment.trackingConfig.gaTrackingId,
        ga4TrackingId: environment.trackingConfig.ga4TrackingId,
        awGoogleConversionId: environment.trackingConfig.awGoogleConversionId,
        facebookPixelId: environment.trackingConfig.facebookPixelId,
        tracesSampleRate: environment.trackingConfig.tracesSampleRate,
        gaCustomDimensionMap: {
          dimension1:
            environment.trackingConfig.gaCustomDimensionMap.dimension1,
          dimension2:
            environment.trackingConfig.gaCustomDimensionMap.dimension2,
          dimension3:
            environment.trackingConfig.gaCustomDimensionMap.dimension3,
        },
        gAdsTrackingId: environment.trackingConfig.gAdsTrackingId,
        gAdsAllowedDomain: environment.trackingConfig.gAdsAllowedDomain,
        linkedInPixelId: environment.trackingConfig.linkedInPixelId,
        quoraPixelId: environment.trackingConfig.quoraPixelId,
        hotjarId: environment.trackingConfig.hotjarId,
      },
    },
    BackgroundGeolocation,
  ],
  exports: [BrowserModule, ModalModule, TrackingModule, UiModule],
})
export class AppCommonModule {}
